import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { useOnSudoLayoutEvent, useSudoLayoutEventEmitter } from "src/lib/layout-events";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { SudoSegmentedControl } from "src/sudokit/components/SudoSegmentedControl";
import { Text } from "src/sudokit/components/Text";
import { PRICING_PLANS } from "../../../../src/shared/constants";
import CardSet from "../../CardSet";
import Favorites from "../../Favorites";
import Feedback from "../../Feedback";
import { useHistory } from "react-router-dom";
import { sendAppEvent } from "../../Utils";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { useAppStore } from "../../stores/appStore";
import { useProjectStore } from "../../stores/projectStore";
import { createSafeStorage } from "../../stores/safeStorage";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import EDUCards from "../EDUCards";
import IconButton from "../IconButton";
import { PersistentChat } from "../PersistentChat";
import { CTAAttentionOverlay } from "../PersistentChat/CTAAttentionOverlay";
import QuotaBar from "../Pricing/QuotaBar";
import StickyNoteAnchor from "../StickyTour/StickyNoteAnchor";
import { AnchorIds } from "../StickyTour/TourTypes";
import EDUCardsSB from "./EDUCardsSB";
import { Spinner } from "src/sudokit/components/Spinner";
import { StyledFlexContainer, HistoryAnimations } from "./styles";

const HISTORY_TAB = 0;
const FAVORITES_TAB = 1;
const getModeKey = (projectId) => `@sudowrite:rightbar-mode:${projectId}`;
const rightBarModeStorage = createSafeStorage(typeof window !== "undefined" ? window.localStorage : null);

function RightBarSubscribe({
  editor,
  cardSetsRef,
  deleteCard,
  demo,
  // documentId,
  existingCards,
  existingCardsLoaded,
  favorites,
  favoritesByCards,
  favoritesStore,
  getSelection,
  highlightInsert,
  insertEditor,
  loadSampleStory,
  loading,
  memory,
  needsFeedback,
  newCards,
  onCardFavorite,
  onCardUnfavorite,
  onChangeTab,
  onDeleteFavorite,
  onFavoriteReorder,
  onNextPage,
  onRedo,
  openBrainstorm,
  openFilter,
  // project,
  setMemory,
  showFront,
  signal,
  // subscription,
  tab,
  totalDocuments,
  updateHideEDUCards,
  updateNeedsFeedback,
  // userData,
  onDeleteCardItem,
  onCardStreamSettled,
  rememberModifier,
}) {
  const [mode, _setMode] = useState(null);
  const [currentChatId, _setCurrentChatId] = useState(null); // string or null
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef(null);

  useOnClickOutside(filterMenuRef, () => setFilterMenuOpen(false));

  const setCurrentChatId = useCallback((chatId) => {
    if (typeof chatId === "string" && chatId.startsWith("{")) {
      _setCurrentChatId(null);
      return;
    }
    _setCurrentChatId(chatId);
  }, []);

  const userData = useSudowriteStore((state) => state.userData);
  const subscriptionJSON = useSudowriteStore((state) => state.subscriptionJSON);
  const project = useProjectStore((state) => state.project);
  const document = useProjectStore((state) => state.document);
  const documentId = document?.id;
  const isSW2 = useAppStore((state) => state.isSW2);
  const emit = useSudoLayoutEventEmitter();

  useOnSudoLayoutEvent("focus_chat", ({ payload }) => {
    setMode("chat");
    emit("focus_chat_input");

    if (payload?.source === "chat-about-an-idea-button") {
      emit("send_introductory_chat_messages");
    }
  });

  const [ctaGlow, setCtaGlow] = useState(false);

  useOnSudoLayoutEvent("trigger_cta_chat", () => {
    setMode("chat");
    setTimeout(() => {
      setCtaGlow(true);
      setTimeout(() => setCtaGlow(false), 3600);
    }, 600);
  });

  useOnSudoLayoutEvent("focus_history", () => {
    setMode("history");
  });

  useEffect(() => {
    // Chat requires a project. Onboarding now runs in a real project, so it
    // can use Chat just like the rest of the app.
    if (!project?.id) {
      setCurrentChatId(null);
      _setMode("history");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, mode]);

  let selector =
    mode === "chat" ? null : (
      <IconButton
        src="/assets/Filter.svg"
        ariaLabel="Filter view options"
        hoverTipText="Filter"
        iconSize={19}
        onClick={(e) => {
          e.preventDefault();
          setFilterMenuOpen(!filterMenuOpen);
        }}
      />
    );

  let content = [];
  let historyStyle = tab === HISTORY_TAB ? { display: "block" } : { display: "none" };
  let favoritesStyle = tab === HISTORY_TAB ? { display: "none" } : { display: "block" };

  let tenPercentLeft = false;

  let allotment;
  let zeroPercentLeft = false;
  if (subscriptionJSON && subscriptionJSON.usesQuota) {
    if (subscriptionJSON.activeTrial) {
      allotment = PRICING_PLANS[subscriptionJSON.data.version].trialQuota;
    } else if (subscriptionJSON.activeSubscription && subscriptionJSON.currentPlan) {
      allotment = subscriptionJSON.currentPlan.allotment;
    }

    if (subscriptionJSON.totalQuota !== undefined) {
      tenPercentLeft = subscriptionJSON.totalQuota <= allotment / 10;
    }

    // if they've claimed grace and not subscribed to a plan, show the bar
    if (subscriptionJSON.grace && !(subscriptionJSON.activeSubscription && subscriptionJSON.currentPlan)) {
      tenPercentLeft = true;
    }

    if (subscriptionJSON.totalQuota <= 0) {
      zeroPercentLeft = true;
    }
  }

  // Card Sets
  let cards = newCards.concat(existingCards);
  cards = cards.map((card, i) => {
    return (
      <CardSet
        openFilter={openFilter}
        getSelection={getSelection}
        signal={signal}
        highlightInsert={highlightInsert}
        key={card.model.clientId}
        position={i}
        deleteCard={deleteCard}
        //favoritesStore={favoritesStore}
        favoritesByCards={favoritesByCards}
        model={card.model}
        error={card.error}
        onRedo={onRedo}
        doneStreaming={card.doneStreaming}
        hidden={card.hidden}
        params={card.params}
        ready={card.ready}
        loading={card.loading}
        initiated={card.initiated}
        onVisible={i === cards.length - 1 ? onNextPage : null}
        insertEditor={insertEditor}
        onFavorite={onCardFavorite}
        onUnfavorite={onCardUnfavorite}
        tabShowing={tab === HISTORY_TAB}
        openBrainstorm={openBrainstorm}
        onDeleteCardItem={onDeleteCardItem}
        onStreamSettled={onCardStreamSettled}
        editor={editor}
        rememberModifier={rememberModifier}
      />
    );
  });

  content.push(
    <div
      id="card-sets"
      className="card-sets"
      key="card-sets"
      style={historyStyle}
      ref={cardSetsRef}
    >
      <HistoryAnimations />
      {!existingCardsLoaded ? (
        <div
          className="flex items-center justify-center py-12"
          style={{ animation: "historyFadeIn 150ms ease-out" }}
        >
          <Spinner size="lg" />
        </div>
      ) : (
        <div key={documentId} style={{ animation: "historyFadeIn 150ms ease-out" }}>
          <Feedback
            documentId={documentId}
            cardCount={newCards.length + existingCards.length}
            enabled={needsFeedback}
            updateNeedsFeedback={updateNeedsFeedback}
          />
          {cards}
          {/* only show the EDUCARDS when viewing the first document in each project */}
          {!isSW2 && project?.contents?.[0]?.id === documentId && !userData?.data?.hideWhatsNewCards && !demo && (
            <EDUCards
              updateHideEDUCards={updateHideEDUCards}
              fullWidth={true}
            />
          )}
          {/* only show the SB2.0 EDUCards when there is no history */}
          {isSW2 && cards.length === 0 && !userData?.data?.hideWhatsNewCards && !demo && (
            <EDUCardsSB
              updateHideEDUCards={updateHideEDUCards}
              fullWidth={true}
              subscription={subscriptionJSON}
              loadSampleStory={loadSampleStory}
            />
          )}
        </div>
      )}
    </div>
  );

  // Favorites

  content.push(
    <div
      style={favoritesStyle}
      key="favorites"
    >
      <Favorites
        tabShowing={tab === FAVORITES_TAB}
        onDeleteFavorite={onDeleteFavorite}
        favorites={favorites}
        insertEditor={insertEditor}
        onFavoriteReorder={onFavoriteReorder}
        editor={editor}
      />
    </div>
  );

  const footer = (
    <>
      {subscriptionJSON && tenPercentLeft && (
        <QuotaBar
          subscription={subscriptionJSON}
          zeroPercentLeft={zeroPercentLeft}
        />
      )}
      <StyledFlexContainer className={classNames("rightbar-footer-buttons-component-container", { sw2: isSW2 })}>
        <StickyNoteAnchor anchorId={AnchorIds.RIGHT_BAR_BOTTOM} />
        {subscriptionJSON && subscriptionJSON.activeTrial && (
          <SubscribeSupportButtons showFront={showFront} />
        )}
      </StyledFlexContainer>
    </>
  );

  const setMode = (newMode) => {
    _setMode(newMode);
    if (project?.id && newMode) {
      rightBarModeStorage.setItem(getModeKey(project.id), newMode);
    }
    if (newMode === "chat") {
      void import("src/lib/editorSelectionContextSync").then(({ reconcileEditorSelectionContextSync }) => {
        reconcileEditorSelectionContextSync();
      });
    }
  };

  useEffect(() => {
    if (mode === null && project?.id) {
      const savedMode = rightBarModeStorage.getItem(getModeKey(project.id));
      setMode(savedMode === "history" ? "history" : "chat");
    }
  }, [mode, project?.id]);

  if (mode === null) return null;

  return (
    <div className="right-panel">
      <CTAAttentionOverlay />
      <StickyNoteAnchor anchorId={AnchorIds.RIGHT_BAR} />
      <div className={classNames("history flex flex-col", { sw2: isSW2, "cta-glow": ctaGlow })}>
        <WithReset
          className="flex justify-between items-center ml-8 -mt-0.5 mb-2 relative z-[100]"
          style={{ zIndex: 100 }}
        >
          <div className="flex-1" />
          <SudoSegmentedControl
            options={
              !project?.id
                ? [{ value: "history", label: "History" }]
                : [
                    { value: "history", label: "History" },
                    { value: "chat", label: "Chat" },
                  ]
            }
            className="-ml-8"
            value={mode}
            onChange={setMode}
            variant="secondary"
            equalWidth={true}
            aria-label="Navigation tabs"
          />
          <div className="flex-1 flex justify-end">
            {!demo && (
              <div className="relative">
                {selector}
                {mode === "history" && filterMenuOpen && (
                  <WithReset
                    className="absolute top-12 right-0 z-[999999] bg-app-menu-color shadow-card border border-app-menu-color-hover p-1 rounded-xl min-w-[160px]"
                    ref={filterMenuRef}
                  >
                    <div className="space-y-3">
                      <Text
                        size="md"
                        weight="medium"
                        color="grey-0"
                        className="mb-3 mt-2 ml-2.5"
                      >
                        View
                      </Text>

                      <div className="flex flex-col gap-2 items-stretch">
                        <SudoButton
                          className={`w-full ${tab === HISTORY_TAB ? "before:bg-[var(--menu-color-hover)] before:rounded-[10px] rounded-[10px]" : ""}`}
                          variant="secondaryGrey"
                          aria-pressed={tab === HISTORY_TAB}
                          onClick={() => {
                            onChangeTab(HISTORY_TAB);
                            setFilterMenuOpen(false);
                          }}
                        >
                          <span className="w-full text-left">All activity</span>
                        </SudoButton>
                        <SudoButton
                          className={`w-full ${tab === FAVORITES_TAB ? "before:bg-[var(--menu-color-hover)] before:rounded-[10px] rounded-[10px]" : ""}`}
                          variant="secondaryGrey"
                          aria-pressed={tab === FAVORITES_TAB}
                          onClick={() => {
                            onChangeTab(FAVORITES_TAB);
                            setFilterMenuOpen(false);
                          }}
                        >
                          <span className="w-full text-left">Favorites</span>
                        </SudoButton>
                      </div>
                    </div>
                  </WithReset>
                )}
              </div>
            )}
          </div>
        </WithReset>

        <div
          className="flex-1 w-full h-[calc(100%-3rem)]"
          style={{ display: mode !== "history" ? "block" : "none" }}
        >
          <PersistentChat
            editor={editor}
            currentChatId={currentChatId}
            setCurrentChatId={setCurrentChatId}
          />
        </div>
        <div
          className={classNames("cards-container flex-1 w-full", { sw2: isSW2 })}
          style={{ display: mode === "history" ? "block" : "none" }}
        >
          {content}
        </div>
      </div>

      {!demo && footer}
    </div>
  );
}

function SubscribeSupportButtons({ showFront }) {
  const history = useHistory();

  const subscribeButtonText = "Upgrade";

  useEffect(() => {
    sendAppEvent("Right Bar Subscribe Button Viewed", {
      buttonText: subscribeButtonText,
    });
  }, []);

  // SW-8538 Phase B: push /app/plans directly. The Pricing page handles
  // EU-vs-non-EU routing (and opens the VAT modal for EU + !hasPaid users).
  const handleUpgradeClick = () => {
    sendAppEvent("Right Bar Subscribe Clicked", {
      ref: "rightbar",
    });

    history.push("/app/plans?ref=rightbar");
  };

  return (
    <div className="flex ml-3 mb-2 mt-0 w-full pr-3">
      <SudoButton
        onClick={(e) => {
          e.preventDefault();
          showFront(true);
        }}
        variant="secondary"
        leftIcon="/assets/more-dialogue.svg"
        aria-label="Contact support"
        className="flex-1"
      >
        Support
      </SudoButton>
      <SudoButton
        onClick={handleUpgradeClick}
        variant="primary"
        leftIcon="/assets/subscribe-arrow.svg"
        aria-label="Upgrade subscription"
        className="flex-1"
      >
        {subscribeButtonText}
      </SudoButton>
    </div>
  );
}

export default RightBarSubscribe;
